/* AuthContext — lightweight provider that owns the academy LMS user state.
 * - On mount, calls /api/auth/me (cookies sent with credentials:'include').
 * - Exposes login(email, pwd), logout(), refreshUser(), and `user`.
 * - `user` shape: null while checking, false when unauthenticated, object when authed.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);

const api = axios.create({ baseURL: API, withCredentials: true });

export function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthed, object = authed

  const refreshUser = useCallback(async () => {
    // Skip the request entirely on public pages where no session cookie was
    // ever set. The httpOnly access_token isn't readable by JS, so we use a
    // companion `zhelta_authed=1` flag cookie set in `auth.py:set_auth_cookies`.
    // No flag → guaranteed unauthed → skip the 401 round-trip + console noise.
    const hasHint = typeof document !== 'undefined'
      && document.cookie.split(';').some((c) => c.trim().startsWith('zhelta_authed='));
    if (!hasHint) {
      setUser(false);
      return null;
    }
    try {
      const { data } = await api.get('/api/auth/me');
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/api/auth/login', { email, password });
    // 2FA gate: backend returns {requires_2fa, challenge, email} instead of a session.
    // The caller is responsible for showing the code prompt, then calling loginWith2fa.
    if (data && data.requires_2fa) return data;
    setUser(data);
    return data;
  }, []);

  const loginWith2fa = useCallback(async (challenge, code) => {
    const { data } = await api.post('/api/auth/2fa/login', { challenge, code });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {/* ignore */}
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, loginWith2fa, logout, refreshUser, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
